import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Alert } from './ui/alert';
import { Badge } from './ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/table';
import { ShieldCheck, Eye, EyeOff, Pencil } from 'lucide-react';
import { useRestaurant } from '../context/RestaurantContext';
import apiClient from '../services/api';
import { accountsService } from '../services/accountsService';

const PROTECTED_ROLE = 'Super Admin';

const EMPTY_STAFF = {
  first_name: '', last_name: '', email: '', phone: '',
  username: '', employee_id: '', password: '', staff_type_id: '',
  // Most staff added here work the till, so POS access starts on and can be changed on the profile later.
  can_login_pos: true,
};

const StaffManagement = () => {
  const { restaurantId } = useRestaurant();
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [newStaff, setNewStaff] = useState(EMPTY_STAFF);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Removed unused activeTab, setActiveTab, navigate, location
  
  // Form data state was removed along with the create/edit UI.

  useEffect(() => {
    fetchStaff();
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const { data } = await apiClient.get('/users/login/roles/');
      // The endpoint shape is { success, data: { roles } }, with fallbacks if it ever changes.
      const list = Array.isArray(data)
        ? data
        : data?.data?.roles
          || (Array.isArray(data?.data) ? data.data : null)
          || data?.results
          || [];
      setRoles(Array.isArray(list) ? list : []);
    } catch (err) {
      // Non-fatal: form will fall back to a blank dropdown
      console.error('Failed to fetch roles', err);
      setRoles([]);
    }
  };

  // Removed tab logic (no tabs/UI switching)

  // Fetch paginated staff list via the unified accounts endpoint (type=staff).
  const fetchStaff = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await accountsService.list({ type: 'staff', page: 1, pageSize: 50 });
      setStaff(data?.results || []);
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      setError(`Network error: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const addStaff = async () => {
    setError('');
    // Client-side guardrails that name only what is actually missing, so nobody re-checks fields that were fine.
    const missing = [
      [newStaff.first_name, 'first name'],
      [newStaff.last_name, 'last name'],
      [newStaff.email, 'email'],
      [newStaff.username, 'username'],
      [newStaff.staff_type_id, 'role'],
    ].filter(([value]) => !String(value || '').trim()).map(([, label]) => label);
    if (missing.length) {
      const list = missing.length === 1
        ? missing[0]
        : `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}`;
      setError(`Please fill in the ${list}.`);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newStaff.email.trim())) {
      setError('That email address doesn’t look right — check it and try again.');
      return;
    }
    // A password is only needed by someone who is getting POS access.
    if (newStaff.can_login_pos && !newStaff.password) {
      setError('Set a password, or switch POS access off for this person.');
      return;
    }
    if (!restaurantId) {
      setError('No restaurant selected — cannot create staff.');
      return;
    }
    try {
      const payload = {
        ...newStaff,
        restaurant_id: restaurantId,
        staff_type_id: Number(newStaff.staff_type_id),
      };
      const { data } = await apiClient.post('/users/user/create/', payload);
      if (!data.success) throw new Error(data.error || 'Failed to add staff');
      setShowAdd(false);
      setShowPassword(false);
      setNewStaff(EMPTY_STAFF);
      fetchStaff();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  // Removed unused fetchStaffTypes function

  // Removed unused handleInputChange and handleSubmit

  // Removed unused handleEdit

  // Removed unused handleDelete

  // Removed unused resetForm

  // Removed password decryption test (non-shadcn dev helper)


  // Only error/success at the top, and w-full rather than `container`, which left the table floating in gutters.
  return (
    <div className="w-full p-6">
      {error && (<Alert className="mb-4" variant="destructive">{error}</Alert>)}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Staff Members ({staff.length})</CardTitle>
            <button className="px-3 py-2 bg-blue-600 text-white rounded" onClick={()=>setShowAdd(true)}>Add Staff</button>
          </div>
        </CardHeader>
        <CardContent>
        
        {loading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading...</p>
          </div>
        )}
        
        {!loading && staff.length === 0 && (
          <p className="p-4 text-gray-500">No staff members found</p>
        )}
        {!loading && staff.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {staff.map((member) => {
                  return (
                  <TableRow key={member.id} onClick={()=>navigate(`/staff/${member.id}`)} className="cursor-pointer hover:bg-gray-50">
                    <TableCell>
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          {member.full_name || `${member.first_name} ${member.last_name}`}
                          {(member.staff_type === PROTECTED_ROLE || member.staff_type_name === PROTECTED_ROLE) && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                              <ShieldCheck size={10} /> Protected
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">{member.employee_id}</div>
                      </div>
                    </TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>{member.staff_type || member.staff_type_name || '-'}</TableCell>
                    <TableCell>{member.phone || '-'}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge variant={member.is_active && (member.status === 'active' || member.status === 'ACTIVE') ? 'default' : 'destructive'}>
                          {member.is_active ? (member.status || 'Inactive') : 'Inactive'}
                        </Badge>
                        {/* Says it on the list, rather than making someone open each profile to find who works the till. */}
                        {member.can_login_pos === false && (
                          <Badge variant="outline" title="Cannot sign in on the POS or Android app">
                            No POS
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e)=>e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={()=>navigate(`/staff/${member.id}`)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
                          title="View staff member"
                        >
                          <Eye size={14} /> View
                        </button>
                        <button
                          type="button"
                          onClick={()=>navigate(`/staff/${member.id}?edit=1`)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm rounded border border-blue-300 text-blue-700 hover:bg-blue-50"
                          title="Edit staff member"
                        >
                          <Pencil size={14} /> Edit
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        </CardContent>
      </Card>

      {showAdd && (
        <Card className="mt-4">
          <CardHeader><CardTitle>Add New Staff</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label>First Name <span className="text-rose-500">*</span></label>
                <input className="border rounded px-2 py-2 w-full" value={newStaff.first_name} onChange={e=>setNewStaff({...newStaff, first_name:e.target.value})} />
              </div>
              <div>
                <label>Last Name <span className="text-rose-500">*</span></label>
                <input className="border rounded px-2 py-2 w-full" value={newStaff.last_name} onChange={e=>setNewStaff({...newStaff, last_name:e.target.value})} />
              </div>
              <div>
                <label>Email <span className="text-rose-500">*</span></label>
                <input type="email" className="border rounded px-2 py-2 w-full" value={newStaff.email} onChange={e=>setNewStaff({...newStaff, email:e.target.value})} />
              </div>
              <div>
                <label>Phone</label>
                <input type="tel" className="border rounded px-2 py-2 w-full" value={newStaff.phone} onChange={e=>setNewStaff({...newStaff, phone:e.target.value})} />
              </div>
              <div>
                <label>Username <span className="text-rose-500">*</span></label>
                <input className="border rounded px-2 py-2 w-full" value={newStaff.username} onChange={e=>setNewStaff({...newStaff, username:e.target.value})} />
              </div>
              <div>
                <label>Employee ID</label>
                <input className="border rounded px-2 py-2 w-full" placeholder="e.g. EMP-014 (optional)" value={newStaff.employee_id} onChange={e=>setNewStaff({...newStaff, employee_id:e.target.value})} />
              </div>

              {/* POS access decides whether a password is wanted at all; someone kept off the till needs none. */}
              <div>
                <label>POS access</label>
                <label className="mt-1 flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={newStaff.can_login_pos}
                    onChange={e => setNewStaff({ ...newStaff, can_login_pos: e.target.checked })}
                  />
                  <span className="text-sm">
                    Can sign in on the POS
                    <span className="block text-xs text-slate-500">
                      The Windows POS and the Android app.
                    </span>
                  </span>
                </label>
              </div>

              {newStaff.can_login_pos && (
                <div>
                  <label>Password <span className="text-rose-500">*</span></label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="border rounded px-2 py-2 w-full pr-9"
                      value={newStaff.password}
                      onChange={e => setNewStaff({ ...newStaff, password: e.target.value })}
                      autoComplete="new-password"
                      placeholder="Set a password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute inset-y-0 right-2 flex items-center text-slate-500 hover:text-slate-800"
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      title={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label>Role <span className="text-rose-500">*</span></label>
                {(() => {
                  const assignable = roles.filter(r => r.name !== PROTECTED_ROLE);
                  if (assignable.length === 0) {
                    return (
                      <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        No assignable roles yet. Create one in{' '}
                        <a href="/roles-management" className="font-semibold underline">Staff → Roles</a>{' '}
                        before adding non-admin staff.
                      </div>
                    );
                  }
                  return (
                    <select
                      className="border rounded px-2 py-2 w-full bg-white"
                      value={newStaff.staff_type_id}
                      onChange={e => setNewStaff({ ...newStaff, staff_type_id: e.target.value })}
                    >
                      <option value="">— Select a role —</option>
                      {assignable.map(r => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                  );
                })()}
              </div>
            </div>

            {/* Inline error — visible right where the user is working */}
            {error && (
              <div className="mt-4 rounded border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                {error}
              </div>
            )}

            <div className="mt-4 space-x-2">
              <button className="px-3 py-2 bg-green-600 text-white rounded" onClick={addStaff}>Save</button>
              <button
                className="px-3 py-2 bg-gray-300 text-gray-800 rounded"
                onClick={() => { setShowAdd(false); setShowPassword(false); setNewStaff(EMPTY_STAFF); setError(''); }}
              >
                Cancel
              </button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StaffManagement;